import { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, Search, Code, Image, FileText, Calculator, Cloud } from 'lucide-react';

interface FunctionCallIndicatorProps {
  functionName?: string;
  isExecuting?: boolean;
  result?: any;
}

export function FunctionCallIndicator({ functionName, isExecuting, result }: FunctionCallIndicatorProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (functionName || isExecuting || result) {
      setIsVisible(true);
    }
  }, [functionName, isExecuting, result]);

  if (!isVisible) return null;

  const getFunctionIcon = (name: string) => {
    switch (name) {
      case 'web_search':
        return <Search className="w-4 h-4" />;
      case 'analyze_code':
        return <Code className="w-4 h-4" />;
      case 'generate_image':
        return <Image className="w-4 h-4" />;
      case 'analyze_document':
        return <FileText className="w-4 h-4" />;
      case 'calculate':
        return <Calculator className="w-4 h-4" />;
      case 'get_weather':
        return <Cloud className="w-4 h-4" />;
      default:
        return <Loader2 className="w-4 h-4" />;
    }
  };

  const getFunctionLabel = (name: string) => {
    switch (name) {
      case 'web_search':
        return 'Pesquisando na web';
      case 'analyze_code':
        return 'Analisando código';
      case 'generate_image':
        return 'Gerando imagem';
      case 'analyze_document':
        return 'Analisando documento';
      case 'calculate':
        return 'Calculando';
      case 'get_weather':
        return 'Obtendo clima';
      default:
        return 'Executando função';
    }
  };

  return (
    <Card className="mb-4 border-blue-200 bg-blue-50">
      <CardContent className="p-3">
        <div className="flex items-center gap-2">
          {isExecuting ? (
            <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
          ) : (
            functionName && getFunctionIcon(functionName)
          )}
          
          <div className="flex-1">
            {isExecuting ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-blue-800">
                  {functionName ? getFunctionLabel(functionName) : 'Executando função'}...
                </span>
                <Badge variant="secondary" className="text-xs">
                  🔧 Função ativa
                </Badge>
              </div>
            ) : result ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-green-800">
                    Função executada com sucesso
                  </span>
                  <Badge variant="outline" className="text-xs text-green-600">
                    ✅ Concluído
                  </Badge>
                </div>
                
                {/* Mostrar preview do resultado baseado no tipo de função */}
                {functionName === 'web_search' && result.data && (
                  <div className="text-xs text-gray-600">
                    Encontrados {result.data.total_results} resultados para "{result.data.query}"
                  </div>
                )}
                
                {functionName === 'analyze_code' && result.data && (
                  <div className="text-xs text-gray-600">
                    Análise de código concluída - {result.data.metrics?.lines_of_code} linhas analisadas
                  </div>
                )}
                
                {functionName === 'generate_image' && result.data && (
                  <div className="text-xs text-gray-600">
                    Imagem gerada: "{result.data.prompt}" ({result.data.size})
                  </div>
                )}
                
                {functionName === 'calculate' && result.data && (
                  <div className="text-xs text-gray-600">
                    {result.data.expression} = {result.data.result}
                  </div>
                )}
                
                {functionName === 'get_weather' && result.data && (
                  <div className="text-xs text-gray-600">
                    {result.data.location}: {result.data.current.temperature}° - {result.data.current.description}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
